/**
 * The in-game HUD.
 *
 * Layout is fixed and generous, because this is read from a sofa:
 *
 *   ┌─ top bar ─ round · phase · live event · turn order ──────────────┐
 *   │                                                    ┌ tech ─┐    │
 *   │                     board (camera area)            │ market│    │
 *   │                                                    └───────┘    │
 *   ├─ four seat panels, one per charter ──────────────────────────────┤
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * `insets()` reports the chrome the camera must avoid, so the board always frames itself
 * into whatever is left rather than sliding under a panel.
 */

import {
  ACTIONS, PLAYER_SHORT, RESOURCE_CAP, MAX_FLEETS, TECH_MARKET_SIZE,
} from '../game/constants';
import { ACTION_ORDER } from './actionOrder';
import { eventCard } from '../game/events';
import { CHARTERS, charterMet, charterProgress } from '../game/charters';
import { TECHS, prerequisiteMet, researchCost } from '../game/tech';
import {
  colonyCount, fleetCount, holdingCount, outpostCount, productionFor, upkeepFor,
} from '../game/queries';
import { activeSeat } from '../game/engine';
import { legalActionKinds } from '../game/actions';
import type {
  ActionKind, GameState, Phase, Player, PlayerId, Resources,
} from '../game/types';
import {
  ease, INK, seatColor, TYPE, withAlpha,
} from './palette';
import {
  chip, hintBar, meter, panel, paragraph, resourceRow, roundRect, screenGrade, seatBadge,
  text,
} from './widgets';

/* ---------------------------------------------------------------------- sizes */

const TOP_BAR_H = 78;
const SEAT_PANEL_H = 162;
const TECH_STRIP_W = 214;
const EDGE = 18;

/**
 * Chrome dimensions, exported so the overlays can lay themselves out in the space the HUD
 * leaves rather than guessing with viewport fractions — which is how a bid column ends up
 * printed over a seat panel.
 */
export const HUD_LAYOUT = {
  topBarHeight: TOP_BAR_H,
  seatPanelHeight: SEAT_PANEL_H,
  techStripWidth: TECH_STRIP_W,
  edge: EDGE,
  /** Top of the seat-panel strip, for a given viewport height. */
  seatPanelTop: (height: number) => height - EDGE - SEAT_PANEL_H,
  /** Bottom of the top bar. */
  contentTop: () => EDGE + TOP_BAR_H + EDGE,
} as const;

/** UI state the HUD needs but the game state does not carry. */
export interface HudView {
  /** Action menu open for the active seat, with the highlighted row. */
  menuOpen: boolean;
  menuIndex: number;
  /** Transient message, e.g. an illegal-action reason. */
  toast: { text: string; seat: PlayerId | null; tone: 'info' | 'bad' | 'good'; until: number } | null;
  /** Big transient "your turn" banner. */
  banner: { text: string; sub: string; seat: PlayerId | null; until: number } | null;
  /** Seconds left on the advisory round clock, or null when disabled. */
  clockSeconds: number | null;
  /** Whether the tech market strip is shown (hidden during bid overlays). */
  showTechMarket: boolean;
  /** Highlighted tech in the market, when the Research action is armed. */
  techIndex: number;
  techArmed: boolean;
}

export function emptyHudView(): HudView {
  return {
    menuOpen: false,
    menuIndex: 0,
    toast: null,
    banner: null,
    clockSeconds: null,
    showTechMarket: true,
    techIndex: 0,
    techArmed: false,
  };
}

const PHASE_LABEL: Record<Phase, string> = {
  setup: 'Setup',
  drift: 'Drift',
  production: 'Production',
  command: 'Command',
  contest: 'Contest',
  auction: 'Charter Auction',
  upkeep: 'Upkeep',
  finalScoring: 'Final Scoring',
  gameOver: 'Match Over',
};

const PHASE_INK: Record<Phase, string> = {
  setup: INK.textDim,
  drift: '#8f7cff',
  production: INK.good,
  command: INK.info,
  contest: INK.bad,
  auction: INK.warn,
  upkeep: INK.textDim,
  finalScoring: '#ffd76b',
  gameOver: '#ffd76b',
};

export class Hud {
  private time = 0;

  update(dtMs: number): void {
    this.time += dtMs;
  }

  /**
   * The HUD's own accumulated clock, in ms.
   *
   * Toast and banner deadlines are compared against this, so whoever *sets* them has to read
   * it from here. Mirroring the accumulation elsewhere drifts, and a drifting deadline means
   * messages that vanish before they can be read.
   */
  get now(): number {
    return this.time;
  }

  /** Chrome the camera must keep the board clear of. */
  insets(width: number, height: number, view: HudView): {
    top: number; right: number; bottom: number; left: number;
  } {
    void height;
    return {
      top: TOP_BAR_H + EDGE,
      right: view.showTechMarket && width > 940 ? TECH_STRIP_W + EDGE : EDGE,
      bottom: SEAT_PANEL_H + EDGE,
      left: EDGE,
    };
  }

  draw(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    view: HudView,
    width: number,
    height: number,
  ): void {
    screenGrade(ctx, width, height);
    this.drawTopBar(ctx, state, view, width);
    if (view.showTechMarket && width > 940) this.drawTechMarket(ctx, state, view, width, height);
    this.drawSeatPanels(ctx, state, width, height);
    if (view.menuOpen) this.drawActionMenu(ctx, state, view, width, height);
    this.drawHints(ctx, state, view, width, height);
    this.drawToast(ctx, view, width, height);
    this.drawBanner(ctx, view, width, height);
  }

  /* ------------------------------------------------------------------ top bar */

  private drawTopBar(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    view: HudView,
    width: number,
  ): void {
    panel(ctx, EDGE, EDGE, width - EDGE * 2, TOP_BAR_H, {
      fill: 'rgba(10, 12, 22, 0.9)',
      corners: { tr: true, bl: true },
      chamfer: 18,
    });

    const midY = EDGE + TOP_BAR_H / 2;
    let cursor = EDGE + 22;

    // Round counter.
    text(ctx, 'ROUND', cursor, midY - 13, {
      size: TYPE.micro, color: INK.textFaint, tracking: 2.2, baseline: 'middle',
    });
    const roundText = `${Math.max(1, state.round)}`;
    const roundW = text(ctx, roundText, cursor, midY + 12, {
      size: TYPE.heading, weight: 800, color: INK.text, baseline: 'middle',
    });
    text(ctx, `/ ${state.settings.totalRounds}`, cursor + roundW + 6, midY + 12, {
      size: TYPE.label, weight: 600, color: INK.textFaint, baseline: 'middle',
    });
    cursor += Math.max(72, roundW + 56);

    // Phase chip.
    cursor += chip(ctx, PHASE_LABEL[state.phase], cursor, midY - 13, {
      color: PHASE_INK[state.phase], filled: true, size: TYPE.label, height: 26,
    }) + 20;

    // Live event.
    const eventId = state.currentEvent;
    const eventRight = width - EDGE - 260;
    if (eventId) {
      const card = eventCard(eventId);
      const tone = card.tone === 'good' ? INK.good : card.tone === 'bad' ? INK.bad : INK.warn;
      const nameW = text(ctx, card.name, cursor, midY - 11, {
        size: TYPE.label, weight: 800, color: tone, tracking: 0.8, baseline: 'middle',
        maxWidth: Math.max(80, eventRight - cursor),
      });
      text(ctx, card.blurb, cursor, midY + 13, {
        size: TYPE.micro, weight: 500, color: INK.textDim, baseline: 'middle',
        maxWidth: Math.max(80, eventRight - cursor),
      });
      void nameW;
    }

    // Turn order pips on the right — who is next is a constant question.
    this.drawTurnOrder(ctx, state, width - EDGE - 22, midY);

    // Advisory clock.
    if (view.clockSeconds !== null) {
      const minutes = Math.floor(Math.max(0, view.clockSeconds) / 60);
      const seconds = Math.floor(Math.max(0, view.clockSeconds) % 60);
      const low = view.clockSeconds < 60;
      text(
        ctx,
        `${minutes}:${String(seconds).padStart(2, '0')}`,
        width - EDGE - 22,
        midY + 22,
        {
          size: TYPE.micro, weight: 700,
          color: low ? INK.warn : INK.textFaint,
          align: 'right', baseline: 'middle',
        },
      );
    }
  }

  /** Seat pips in this round's turn order, with the active seat enlarged. */
  private drawTurnOrder(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    rightX: number,
    y: number,
  ): void {
    const active = activeSeat(state);
    const pip = 13;
    const gap = 10;
    const totalW = state.turnOrder.length * (pip * 2 + gap) - gap;
    let x = rightX - totalW + pip;

    for (const seat of state.turnOrder) {
      const player = state.players[seat]!;
      const isActive = seat === active;
      const done = player.passed;

      ctx.save();
      if (done && !isActive) ctx.globalAlpha = 0.32;
      seatBadge(ctx, seat, x, y - 8, isActive ? pip * 1.12 : pip * 0.86, {
        active: isActive,
        label: PLAYER_SHORT[seat],
      });
      ctx.restore();

      if (isActive) {
        // Little caret under the seat currently holding the baton.
        ctx.save();
        ctx.fillStyle = seatColor(seat);
        ctx.beginPath();
        ctx.moveTo(x, y + 12);
        ctx.lineTo(x - 5, y + 6);
        ctx.lineTo(x + 5, y + 6);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      x += pip * 2 + gap;
    }
  }

  /* -------------------------------------------------------------- seat panels */

  private drawSeatPanels(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    width: number,
    height: number,
  ): void {
    const gap = 12;
    const totalW = width - EDGE * 2;
    const panelW = (totalW - gap * 3) / 4;
    const y = height - EDGE - SEAT_PANEL_H;
    const active = activeSeat(state);

    state.players.forEach((player, index) => {
      const x = EDGE + index * (panelW + gap);
      this.drawSeatPanel(ctx, state, player, x, y, panelW, SEAT_PANEL_H, player.id === active);
    });
  }

  private drawSeatPanel(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    player: Player,
    x: number, y: number, w: number, h: number,
    isActive: boolean,
  ): void {
    const color = seatColor(player.id);
    const pulse = ease.pulse((this.time % 1800) / 1800);

    panel(ctx, x, y, w, h, {
      fill: isActive ? withAlpha(color, 0.13) : 'rgba(10, 12, 22, 0.88)',
      border: isActive ? withAlpha(color, 0.7 + pulse * 0.3) : INK.edge,
      borderWidth: isActive ? 2.4 : 1.4,
      accent: color,
      accentWidth: 5,
      glow: isActive ? withAlpha(color, 0.4) : undefined,
      corners: { tr: true },
    });

    const pad = 16;
    let cursorY = y + 26;

    // Header: badge, name, VP.
    seatBadge(ctx, player.id, x + pad + 12, cursorY, 12, { label: PLAYER_SHORT[player.id] });

    const nameRight = x + w - pad - 54;
    text(ctx, player.name, x + pad + 32, cursorY - 5, {
      size: TYPE.label, weight: 800, color: INK.text, baseline: 'middle',
      maxWidth: Math.max(40, nameRight - (x + pad + 32)),
    });
    text(
      ctx,
      player.kind === 'ai' ? 'AUTARCH · AI' : player.gamepadIndex !== null ? 'PAD' : 'KEYS',
      x + pad + 32,
      cursorY + 11,
      { size: TYPE.micro, weight: 600, color: INK.textFaint, baseline: 'middle', tracking: 1 },
    );

    // VP — the number everybody is actually tracking, so it is the biggest thing here.
    text(ctx, String(player.vp), x + w - pad, cursorY + 2, {
      size: TYPE.title * 0.82, weight: 800, color: INK.text,
      align: 'right', baseline: 'middle',
      shadow: withAlpha(color, 0.55),
    });
    text(ctx, 'VP', x + w - pad, cursorY + 24, {
      size: TYPE.micro, weight: 700, color: INK.textFaint, align: 'right', baseline: 'middle',
      tracking: 1.6,
    });

    cursorY += 44;

    // Resource row, with next-round production as a delta.
    const production = productionFor(state, player.id);
    resourceRow(ctx, player.resources, x + pad, cursorY, {
      size: TYPE.label,
      gap: Math.max(8, (w - pad * 2 - 130) / 3),
      deltas: state.phase === 'command' ? production : undefined,
      cap: RESOURCE_CAP,
    });

    cursorY += 26;

    // Command points as discrete pips — the resource that gates every turn.
    text(ctx, 'CP', x + pad, cursorY + 5, {
      size: TYPE.micro, weight: 700, color: INK.textFaint, baseline: 'middle', tracking: 1.2,
    });
    meter(ctx, x + pad + 24, cursorY, w * 0.32, 8, player.cp, Math.max(player.cp, 7), {
      color: player.passed ? INK.textFaint : INK.info,
      segments: Math.max(player.cp, 7),
    });

    // Population, with a starvation warning when upkeep cannot be met.
    const upkeep = upkeepFor(state, player.id);
    const starving = state.round % 2 === 0 && player.resources.biomass < upkeep;
    text(ctx, 'POP', x + w * 0.52, cursorY + 5, {
      size: TYPE.micro, weight: 700, color: INK.textFaint, baseline: 'middle', tracking: 1.2,
    });
    meter(ctx, x + w * 0.52 + 32, cursorY, w * 0.28, 8, player.population, 12, {
      color: starving ? INK.bad : INK.good,
    });
    text(ctx, String(player.population), x + w - pad, cursorY + 5, {
      size: TYPE.micro, weight: 800, color: starving ? INK.bad : INK.textDim,
      align: 'right', baseline: 'middle',
    });

    cursorY += 24;

    // Holdings summary.
    const parts = [
      `${outpostCount(state, player.id)}⬢`,
      `${colonyCount(state, player.id)}⬣`,
      `${fleetCount(state, player.id)}/${MAX_FLEETS}➤`,
      `${player.techs.length}⚛`,
    ];
    text(ctx, parts.join('   '), x + pad, cursorY + 6, {
      size: TYPE.micro, weight: 700, color: INK.textDim, baseline: 'middle', tracking: 0.4,
    });

    // Charters, with a met/unmet tick — the endgame in miniature.
    if (player.charters.length > 0) {
      let chipX = x + pad;
      const chipY = cursorY + 16;
      for (const id of player.charters.slice(0, 2)) {
        const met = charterMet(state, player, id);
        const [done, need] = charterProgress(state, player, id);
        chipX += chip(
          ctx,
          `${CHARTERS[id].name} ${met ? '✓' : `${done}/${need}`}`,
          chipX, chipY,
          { color: met ? INK.good : INK.textDim, size: TYPE.micro - 1, height: 19, padding: 6 },
        ) + 5;
      }
      if (player.charters.length > 2) {
        text(ctx, `+${player.charters.length - 2}`, chipX + 2, chipY + 10, {
          size: TYPE.micro, weight: 700, color: INK.textFaint, baseline: 'middle',
        });
      }
    }

    // Relic / megastructure badges tuck into the bottom-right.
    let badgeX = x + w - pad;
    if (player.megastructure) {
      text(ctx, '✺', badgeX, y + h - 16, {
        size: TYPE.body, color: INK.warn, align: 'right', baseline: 'middle',
      });
      badgeX -= 22;
    }
    if (player.relics.length > 0) {
      text(ctx, `${player.relics.length}✧`, badgeX, y + h - 16, {
        size: TYPE.micro, weight: 700, color: '#ffd76b', align: 'right', baseline: 'middle',
      });
    }
  }

  /* -------------------------------------------------------------- tech market */

  private drawTechMarket(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    view: HudView,
    width: number,
    height: number,
  ): void {
    const x = width - EDGE - TECH_STRIP_W;
    const y = EDGE + TOP_BAR_H + EDGE;
    const available = height - y - SEAT_PANEL_H - EDGE * 2;
    const cardH = Math.min(96, (available - 30 - 8 * 3) / TECH_MARKET_SIZE);

    text(ctx, 'TECH MARKET', x + 4, y + 8, {
      size: TYPE.micro, weight: 800, color: INK.textFaint, tracking: 2.4, baseline: 'middle',
    });

    const seat = activeSeat(state);
    const player = seat !== null ? state.players[seat]! : null;

    state.techMarket.forEach((id, index) => {
      const tech = TECHS[id];
      const cardY = y + 24 + index * (cardH + 8);
      const affordable = player
        ? prerequisiteMet(player, id) && player.resources.data >= researchCost(player, id)
        : false;
      const highlighted = view.techArmed && view.techIndex === index;

      panel(ctx, x, cardY, TECH_STRIP_W, cardH, {
        fill: highlighted ? withAlpha(INK.info, 0.2) : 'rgba(10, 12, 22, 0.86)',
        border: highlighted ? INK.info : affordable ? withAlpha(INK.good, 0.5) : INK.edge,
        borderWidth: highlighted ? 2.2 : 1.3,
        chamfer: 10,
      });

      ctx.save();
      if (!affordable) ctx.globalAlpha = 0.55;

      text(ctx, tech.name, x + 11, cardY + 17, {
        size: TYPE.label - 1, weight: 800, color: INK.text, maxWidth: TECH_STRIP_W - 52,
        baseline: 'middle',
      });
      text(ctx, `T${tech.tier}`, x + TECH_STRIP_W - 11, cardY + 17, {
        size: TYPE.micro, weight: 800, color: INK.textFaint, align: 'right', baseline: 'middle',
      });

      paragraph(ctx, tech.blurb, x + 11, cardY + 36, TECH_STRIP_W - 22, {
        size: TYPE.micro - 0.5, weight: 500, color: INK.textDim, lineHeight: 13.5,
      });

      const cost = player ? researchCost(player, id) : tech.cost;
      text(ctx, `◈ ${cost}`, x + 11, cardY + cardH - 13, {
        size: TYPE.micro, weight: 800, color: affordable ? INK.info : INK.textFaint,
        baseline: 'middle',
      });
      text(ctx, `${tech.vp} VP`, x + TECH_STRIP_W - 11, cardY + cardH - 13, {
        size: TYPE.micro, weight: 800, color: '#ffd76b', align: 'right', baseline: 'middle',
      });
      ctx.restore();
    });

    if (state.techMarket.length === 0) {
      text(ctx, 'Exhausted', x + TECH_STRIP_W / 2, y + 60, {
        size: TYPE.label, color: INK.textFaint, align: 'center', baseline: 'middle',
      });
    }
  }

  /* ------------------------------------------------------------- action menu */

  /**
   * The action list. A flat vertical menu rather than a radial dial: with ten actions a
   * radial gets ambiguous on a thumbstick, and a list can show the CP cost and the reason
   * an action is unavailable, which is what players actually need.
   */
  private drawActionMenu(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    view: HudView,
    width: number,
    height: number,
  ): void {
    const seat = activeSeat(state);
    if (seat === null) return;
    const player = state.players[seat]!;
    const legal = new Set(legalActionKinds(state, seat));

    const rowH = 40;
    const menuW = 330;
    const menuH = ACTION_ORDER.length * rowH + 62;
    const x = Math.min(width - menuW - EDGE, Math.max(EDGE, width / 2 - menuW / 2));
    const y = Math.max(EDGE + TOP_BAR_H + EDGE, height / 2 - menuH / 2 - 40);
    const color = seatColor(seat);

    // Scrim so the menu reads over a busy starfield.
    ctx.save();
    ctx.fillStyle = 'rgba(4, 5, 10, 0.55)';
    ctx.fillRect(0, 0, width, height);
    ctx.restore();

    panel(ctx, x, y, menuW, menuH, {
      fill: 'rgba(10, 12, 22, 0.97)',
      border: withAlpha(color, 0.8),
      borderWidth: 2.2,
      accent: color,
      glow: withAlpha(color, 0.35),
      chamfer: 18,
    });

    text(ctx, `${player.name} — choose an action`, x + 18, y + 24, {
      size: TYPE.label, weight: 800, color, baseline: 'middle', maxWidth: menuW - 100,
    });
    text(ctx, `${player.cp} CP`, x + menuW - 18, y + 24, {
      size: TYPE.label, weight: 800, color: INK.info, align: 'right', baseline: 'middle',
    });

    ACTION_ORDER.forEach((kind, index) => {
      const def = ACTIONS[kind];
      const rowY = y + 44 + index * rowH;
      const selected = index === view.menuIndex;
      const usable = legal.has(kind) && player.cp >= def.cp;

      if (selected) {
        roundRect(ctx, x + 8, rowY, menuW - 16, rowH - 4, 8);
        ctx.fillStyle = withAlpha(color, usable ? 0.26 : 0.1);
        ctx.fill();
        ctx.strokeStyle = withAlpha(color, usable ? 0.9 : 0.4);
        ctx.lineWidth = 1.6;
        ctx.stroke();
      }

      ctx.save();
      if (!usable) ctx.globalAlpha = 0.38;

      text(ctx, def.glyph, x + 26, rowY + rowH / 2 - 2, {
        size: TYPE.body, color: usable ? INK.text : INK.textFaint,
        align: 'center', baseline: 'middle',
      });
      text(ctx, def.name, x + 46, rowY + rowH / 2 - 2, {
        size: TYPE.label, weight: 700, color: INK.text, baseline: 'middle',
      });
      text(ctx, def.cp > 0 ? `${def.cp} CP` : '—', x + menuW - 20, rowY + rowH / 2 - 2, {
        size: TYPE.micro, weight: 800,
        color: player.cp >= def.cp ? INK.info : INK.bad,
        align: 'right', baseline: 'middle',
      });
      ctx.restore();
    });

    // Blurb for the highlighted action, so nobody has to memorise the rules.
    const selectedKind = ACTION_ORDER[view.menuIndex];
    if (selectedKind) {
      paragraph(ctx, ACTIONS[selectedKind].blurb, x + 18, y + menuH + 16, menuW - 36, {
        size: TYPE.micro, weight: 500, color: INK.textDim, lineHeight: 15,
      });
    }
  }

  /* ------------------------------------------------------------------- hints */

  private drawHints(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    view: HudView,
    width: number,
    height: number,
  ): void {
    // The bid overlay covers the screen and prints its own hints; a second set behind it
    // just reads as duplicate chrome.
    if (state.phase === 'contest' || state.phase === 'auction') return;

    const y = height - EDGE - SEAT_PANEL_H - 20;
    const hints: { button: string; label: string; dim?: boolean }[] = [];

    if (view.menuOpen) {
      hints.push({ button: 'a', label: 'Choose' }, { button: 'b', label: 'Back' });
    } else if (state.phase === 'command') {
      hints.push(
        { button: 'x', label: 'Actions' },
        { button: 'a', label: 'Confirm' },
        { button: 'y', label: 'Inspect' },
        { button: 'lb', label: 'Fleets' },
        { button: 'rb', label: 'Colonies' },
      );
    } else {
      hints.push({ button: 'a', label: 'Continue' });
    }
    hints.push({ button: 'back', label: 'Scores', dim: true }, { button: 'start', label: 'Menu', dim: true });

    hintBar(ctx, hints, EDGE + 8, y, { size: TYPE.micro + 1, gap: 20 });
    void width;
  }

  /* ------------------------------------------------------------------- toast */

  private drawToast(
    ctx: CanvasRenderingContext2D,
    view: HudView,
    width: number,
    height: number,
  ): void {
    const toast = view.toast;
    if (!toast || this.time > toast.until) return;

    const remaining = toast.until - this.time;
    const alpha = Math.min(1, remaining / 260);
    const tone = toast.tone === 'bad' ? INK.bad : toast.tone === 'good' ? INK.good : INK.info;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.font = `700 ${TYPE.body}px sans-serif`;
    const w = Math.min(width - 80, ctx.measureText(toast.text).width + 64);
    const x = width / 2 - w / 2;
    const y = height - EDGE - SEAT_PANEL_H - 76;

    panel(ctx, x, y, w, 46, {
      fill: 'rgba(8, 10, 18, 0.95)',
      border: withAlpha(tone, 0.85),
      borderWidth: 2,
      accent: toast.seat !== null ? seatColor(toast.seat) : tone,
      chamfer: 12,
    });
    text(ctx, toast.text, x + w / 2 + 3, y + 24, {
      size: TYPE.body, weight: 700, color: INK.text, align: 'center', baseline: 'middle',
      maxWidth: w - 40,
    });
    ctx.restore();
  }

  /* ------------------------------------------------------------------ banner */

  /** The "your turn" / phase-change sweep. Deliberately large and brief. */
  private drawBanner(
    ctx: CanvasRenderingContext2D,
    view: HudView,
    width: number,
    height: number,
  ): void {
    const banner = view.banner;
    if (!banner || this.time > banner.until) return;

    const total = 1300;
    const elapsed = total - (banner.until - this.time);
    const t = Math.max(0, Math.min(1, elapsed / total));
    // Slide in, hold, slide out.
    const slide = t < 0.18
      ? ease.outCubic(t / 0.18)
      : t > 0.8 ? 1 - ease.outCubic((t - 0.8) / 0.2) : 1;
    const color = banner.seat !== null ? seatColor(banner.seat) : INK.info;

    ctx.save();
    ctx.globalAlpha = slide;
    const bandH = 108;
    const y = height * 0.34 - bandH / 2;
    const offset = (1 - slide) * width * 0.14;

    const grad = ctx.createLinearGradient(0, y, width, y + bandH);
    grad.addColorStop(0, 'rgba(6, 7, 14, 0)');
    grad.addColorStop(0.2, withAlpha(color, 0.24));
    grad.addColorStop(0.5, withAlpha(color, 0.34));
    grad.addColorStop(0.8, withAlpha(color, 0.24));
    grad.addColorStop(1, 'rgba(6, 7, 14, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, y, width, bandH);

    ctx.strokeStyle = withAlpha(color, 0.7);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.moveTo(0, y + bandH);
    ctx.lineTo(width, y + bandH);
    ctx.stroke();

    text(ctx, banner.text, width / 2 + offset, y + bandH * 0.42, {
      size: TYPE.title, weight: 800, color: INK.text, align: 'center', baseline: 'middle',
      tracking: 2.6, shadow: withAlpha(color, 0.8), uppercase: true,
    });
    if (banner.sub) {
      text(ctx, banner.sub, width / 2 + offset, y + bandH * 0.76, {
        size: TYPE.label, weight: 600, color: INK.textDim, align: 'center', baseline: 'middle',
        tracking: 1.6,
      });
    }
    ctx.restore();
  }

  /* ---------------------------------------------------------------- utilities */

  /** Seat panel rect, so the notice bus can fly resource gains into the right panel. */
  seatPanelAnchor(seat: PlayerId, width: number, height: number): { x: number; y: number } {
    const gap = 12;
    const panelW = (width - EDGE * 2 - gap * 3) / 4;
    return {
      x: EDGE + seat * (panelW + gap) + panelW / 2,
      y: height - EDGE - SEAT_PANEL_H + 40,
    };
  }

  /** Production summary line for the log/toast, e.g. "+2◆ +1▲". */
  static formatGain(gained: Resources): string {
    const parts: string[] = [];
    if (gained.ore) parts.push(`+${gained.ore}◆`);
    if (gained.fuel) parts.push(`+${gained.fuel}▲`);
    if (gained.biomass) parts.push(`+${gained.biomass}❋`);
    if (gained.data) parts.push(`+${gained.data}◈`);
    return parts.join(' ');
  }

  /** Holdings count, used by the scoreboard overlay. */
  static holdings(state: GameState, seat: PlayerId): number {
    return holdingCount(state, seat);
  }
}

export { ACTION_ORDER };
export type { ActionKind };
